import React, { useState, useEffect, useCallback } from 'react';
import {
  BellContainer,
  BellIcon,
  Badge,
  Dropdown,
  NotificationItem,
  NotificationTitle,
  NotificationBody,
  NotificationTime,
  EmptyState,
} from './styles';
import axios from 'axios';

interface Notification {
  id: number;
  title: string;
  body: string;
  data: any;
  read_at: string | null;
  created_at: string;
}

interface NotificationBellProps {
  userId: number;
  onNotificationClick?: (conversationId: number) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ userId, onNotificationClick }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Usar variável de ambiente ou URL relativa
  const API_URL = process.env.REACT_APP_API_URL || window.location.origin + '/api';

  const loadNotifications = useCallback(async () => {
    try {
      console.log('NotificationBell: Carregando notificações...');
      const response = await axios.get<Notification[]>(`${API_URL}/notifications`, {
        withCredentials: true,
      });
      console.log(`NotificationBell: ${response.data.length} notificações carregadas do backend`);
      
      // Calcular não lidas imediatamente
      const unread = response.data.filter((n) => !n.read_at).length;
      console.log(`NotificationBell: ${unread} notificações não lidas encontradas (de ${response.data.length} total)`);
      
      // Atualizar ambos os estados de forma síncrona
      setNotifications(response.data);
      // Forçar atualização do contador imediatamente
      setUnreadCount(unread);
      console.log(`NotificationBell: Estados atualizados - unreadCount=${unread}, notifications.length=${response.data.length}`);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      // Em caso de erro, manter estado atual mas logar
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  useEffect(() => {
    // Carregar notificações quando componente monta ou userId muda
    console.log(`NotificationBell: Componente montado/atualizado para userId=${userId}, carregando notificações...`);
    loadNotifications();
    
    // Escutar notificações FCM (push notifications)
    // NOTA: Não mostrar notificação aqui para evitar duplicação
    // A notificação nativa já é mostrada no App.tsx e no service worker
    const handleFCMNotification = async (event?: Event) => {
      console.log('NotificationBell: Notificação FCM recebida, recarregando...');
      
      // Aguardar um pouco para garantir que o backend processou
      // e então recarregar notificações
      // Usar um timeout maior para garantir que o backend salvou no arquivo JSON
      setTimeout(() => {
        console.log('NotificationBell: Recarregando notificações após FCM...');
        loadNotifications();
      }, 800);
    };
    
    window.addEventListener('fcmNotificationReceived', handleFCMNotification as EventListener);
    
    return () => {
      window.removeEventListener('fcmNotificationReceived', handleFCMNotification as EventListener);
    };
  }, [userId, loadNotifications]);
  
  // Recarregar notificações quando a página ganha foco (volta para a aba)
  useEffect(() => {
    const handleFocus = () => {
      console.log('NotificationBell: Página ganhou foco, recarregando notificações...');
      loadNotifications();
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('NotificationBell: Página ficou visível, recarregando notificações...');
        loadNotifications();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadNotifications]);
  
  useEffect(() => {
    // Recarregar notificações quando dropdown abre
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  useEffect(() => {
    // Atualizar contador quando notificações forem carregadas
    // Este é um fallback caso loadNotifications não atualize diretamente
    const unread = notifications.filter((n) => !n.read_at).length;
    console.log(`NotificationBell: useEffect contador - calculado: ${unread}, atual: ${unreadCount}, total: ${notifications.length}`);
    if (unread !== unreadCount) {
      console.log(`NotificationBell: Contador desatualizado! Atualizando de ${unreadCount} para ${unread}`);
      setUnreadCount(unread);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const markAsRead = async (id: number) => {
    try {
      await axios.put(
        `${API_URL}/notifications/${id}/read`,
        {},
        {
          withCredentials: true,
        }
      );
      loadNotifications();
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${API_URL}/notifications/read-all`,
        {},
        {
          withCredentials: true,
        }
      );
      loadNotifications();
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    // Abrir chat diretamente sem recarregar página (usando callback do App)
    // Apenas notificação nativa (clique na notificação do sistema) deve usar URL
    if (notification.data?.conversation_id) {
      const conversationId = parseInt(notification.data.conversation_id);
      if (onNotificationClick) {
        onNotificationClick(conversationId);
      } else {
        // Fallback: se não tiver callback, usar evento customizado
        window.dispatchEvent(new CustomEvent('openChat', { 
          detail: { conversationId } 
        }));
      }
    }
    setIsOpen(false);
  };

  // Debug: logar o estado atual antes de renderizar (apenas quando muda)
  useEffect(() => {
    console.log(`NotificationBell: Estado atual - unreadCount=${unreadCount}, notifications.length=${notifications.length}`);
  }, [unreadCount, notifications.length]);

  return (
    <BellContainer>
      <BellIcon onClick={() => setIsOpen(!isOpen)}>
        <span role="img" aria-label="Notificações">🔔</span>
        {unreadCount > 0 && <Badge>{unreadCount}</Badge>}
      </BellIcon>

      {isOpen && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 998,
            }}
            onClick={() => setIsOpen(false)}
          />
          <Dropdown>
            <div style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>
                Notificações {unreadCount > 0 && `(${unreadCount})`}
              </span>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0066cc',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f0f7ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {notifications.length === 0 ? (
                <EmptyState>Nenhuma notificação</EmptyState>
              ) : (
                notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    unread={!notification.read_at}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <NotificationTitle>{notification.title}</NotificationTitle>
                    <NotificationBody>{notification.body}</NotificationBody>
                    <NotificationTime>
                      {new Date(notification.created_at).toLocaleString('pt-BR')}
                    </NotificationTime>
                  </NotificationItem>
                ))
              )}
            </div>
          </Dropdown>
        </>
      )}
    </BellContainer>
  );
};

export default NotificationBell;
